import React, { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Music, ChevronRight, Loader2, Play, CheckCircle, AlertTriangle, XCircle, Database } from 'lucide-react'

interface PlaylistQueue {
  id: string
  name: string
  instance: string
  displayName: string
  playlistName?: string
  statistics?: {
    totalMixPairs: number
    successfulMixes: number
    bpmMismatchCount: number
    failureCount: number
  }
}

export const PlaylistQueuesPage: React.FC = () => {
  const { instance } = useParams<{ instance: string }>()
  const navigate = useNavigate()
  const [queues, setQueues] = useState<PlaylistQueue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!instance) {
      navigate('/')
      return
    }

    const fetchQueues = async () => {
      try {
        const response = await fetch(`/api/experiments/${instance}`)
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('实验实例不存在')
          }
          throw new Error('Failed to fetch playlist queues')
        }
        const data = await response.json()
        
        // 获取每个队列的 mixinfo 数据来提取 playlist_name 和 statistics
        const queuesWithData = await Promise.all(
          data.queues.map(async (queue: PlaylistQueue) => {
            try {
              const mixinfoResponse = await fetch(`/api/experiments/${instance}/${queue.id}/mixinfo`)
              if (mixinfoResponse.ok) {
                const mixinfoData = await mixinfoResponse.json()
                
                // 解析统计信息
                let bpmMismatchCount = 0
                let failureCount = 0
                
                if (mixinfoData.playlist_statistics) {
                  const stats = mixinfoData.playlist_statistics
                  
                  // 统计 BPM 不匹配 (2201) 的数量
                  if (stats.failuresByStatusCode) {
                    const bpmMismatch = stats.failuresByStatusCode.find((item: any) => item.statusCode === 2201)
                    bpmMismatchCount = bpmMismatch ? bpmMismatch.count : 0
                  }
                  
                  // 统计其他失败情况 (2402 和其他)
                  if (stats.failuresByStatusCode) {
                    const otherFailures = stats.failuresByStatusCode.filter((item: any) => item.statusCode !== 2201)
                    failureCount = otherFailures.reduce((sum: number, item: any) => sum + item.count, 0)
                  }
                }
                
                return {
                  ...queue,
                  playlistName: mixinfoData.playlist_name || queue.displayName,
                  statistics: {
                    totalMixPairs: mixinfoData.playlist_statistics?.totalMixPairs || 0,
                    successfulMixes: mixinfoData.playlist_statistics?.successfulMixes || 0,
                    bpmMismatchCount,
                    failureCount
                  }
                }
              }
            } catch (err) {
              console.warn(`Failed to fetch mixinfo for queue ${queue.id}:`, err)
            }
            return {
              ...queue,
              playlistName: queue.displayName,
              statistics: {
                totalMixPairs: 0,
                successfulMixes: 0,
                bpmMismatchCount: 0,
                failureCount: 0
              }
            }
          })
        )
        
        setQueues(queuesWithData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchQueues()
  }, [instance, navigate])

  const instanceDisplayName = instance?.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) || ''

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回首页
          </Link>
          <span className="text-gray-400 dark:text-gray-500">/</span>
          <span className="text-gray-900 dark:text-white font-medium">{instanceDisplayName}</span>
        </div>
        
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
          播放队列列表
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400">
          选择一个播放队列来查看详细的混音分析
        </p>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">加载播放队列...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
          <div className="text-red-600 dark:text-red-400 mb-2">加载失败</div>
          <div className="text-red-500 dark:text-red-300 text-sm">{error}</div>
          <div className="mt-4 space-x-4">
            <Link
              to="/"
              className="inline-block text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </div>
      ) : queues.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
          <Music className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <div className="text-gray-600 dark:text-gray-400 mb-2">暂无播放队列</div>
          <div className="text-gray-500 dark:text-gray-300 text-sm">此实验实例下暂无可用的播放队列</div>
          <div className="mt-4 space-x-4">
            <Link
              to="/"
              className="inline-block text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {queues.map((queue) => (
            <Link
              key={queue.id}
              to={`/experiments/${instance}/${queue.id}`}
              className="block bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200 group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-primary-100 w-10 h-10 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                  <Play className="h-5 w-5 text-primary-600" />
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 transition-colors break-words leading-tight">
                {queue.id}
              </h3>
              
              <div className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                {queue.playlistName}
              </div>

              {/* Statistics Icons */}
              {/* Statistics Icons */}
              {queue.statistics && (
                <div className="flex items-center space-x-3 mb-3">
                  {/* Successful Mixes - Green */}
                  {queue.statistics.successfulMixes > 0 && (
                    <div className="flex items-center space-x-1 text-xs text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span>
                        {queue.statistics.successfulMixes}
                        {queue.statistics.totalMixPairs > 0 && (
                          <span className="text-gray-500 ml-1">
                            ({Math.round((queue.statistics.successfulMixes / queue.statistics.totalMixPairs) * 100)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  
                  {/* BPM Mismatch - Yellow */}
                  {queue.statistics.bpmMismatchCount > 0 && (
                    <div className="flex items-center space-x-1 text-xs text-yellow-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span>
                        {queue.statistics.bpmMismatchCount}
                        {queue.statistics.totalMixPairs > 0 && (
                          <span className="text-gray-500 ml-1">
                            ({Math.round((queue.statistics.bpmMismatchCount / queue.statistics.totalMixPairs) * 100)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  
                  {/* Other Failures - Red */}
                  {queue.statistics.failureCount > 0 && (
                    <div className="flex items-center space-x-1 text-xs text-red-600">
                      <XCircle className="h-4 w-4" />
                      <span>
                        {queue.statistics.failureCount}
                        {queue.statistics.totalMixPairs > 0 && (
                          <span className="text-gray-500 ml-1">
                            ({Math.round((queue.statistics.failureCount / queue.statistics.totalMixPairs) * 100)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  
                  {/* Total Mix Pairs - Blue, at the end */}
                  <div className="flex items-center space-x-1 text-xs text-blue-600 ml-auto">
                    <Database className="h-4 w-4" />
                    <span>{queue.statistics.totalMixPairs}</span>
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Footer Info */}
      <div className="mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
        <div className="flex items-start space-x-4">
          <Music className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">关于播放队列</h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-3">
              每个播放队列包含一系列音频轨道和过渡效果。基于混音算法分析，系统会为每对轨道生成最优的混音参数，
              包括同步点、音量调节和过渡时长等。
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Play className="h-4 w-4 text-blue-500" />
                <span className="text-gray-700">双轨道可视化</span>
              </div>
              <div className="flex items-center space-x-2">
                <Play className="h-4 w-4 text-blue-500" />
                <span className="text-gray-700">实时播放控制</span>
              </div>
              <div className="flex items-center space-x-2">
                <Music className="h-4 w-4 text-blue-500" />
                <span className="text-gray-700">混音质量分析</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
